import Icon from "@afa-shared/afa-components/dist/Icon";
import BreadcrumbsWrapper, { Breadcrumb } from "@components/client/Breadcrumbs";
import { BreadCrumbItem as LayoutBreadCrumbItem } from "@containers/Layout/PageHeader/headerFactory";
import clsx from "clsx";
import { ReactNode, cloneElement, isValidElement } from "react";

import breadcrumbStyles from "./breadcrumbs.module.css";

type BreadCrumbSource = LayoutBreadCrumbItem | ReactNode | null | undefined;

export interface IBreadCrumbsProps {
  breadCrumbLinks: BreadCrumbSource[];
  ariaLabel?: string;
  className?: string;
}

const isLayoutBreadCrumbItem = (value: BreadCrumbSource): value is LayoutBreadCrumbItem =>
  Boolean(
    value &&
      typeof value === "object" &&
      "name" in value &&
      "url" in value &&
      typeof value.name === "string" &&
      typeof value.url === "string",
  );

const deriveSlug = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  const trimmedUrl = url.endsWith("/") ? url.slice(0, -1) : url;
  const segments = trimmedUrl.split("/").filter(Boolean);
  return segments.pop();
};

const AfaBreadCrumbs = ({
  breadCrumbLinks,
  ariaLabel = "Brödsmulor",
  className,
}: IBreadCrumbsProps): React.ReactNode | null => {
  const normalizedLinks = (breadCrumbLinks ?? []).filter(
    (link): link is Exclude<BreadCrumbSource, null | undefined> => Boolean(link),
  );

  if (normalizedLinks.length < 2) {
    return null;
  }

  const navClassName = clsx(breadcrumbStyles.breadcrumbs, className);

  return (
    <BreadcrumbsWrapper>
      <nav className={navClassName} aria-label={ariaLabel}>
        <div className={breadcrumbStyles.breadcrumbHelper}></div>
        <div className={breadcrumbStyles.breadcrumbsListWrapper}>
          <ol className={breadcrumbStyles.breadcrumbsList}>
            {normalizedLinks.map((link, index) => {
              const isLast = index === normalizedLinks.length - 1;

              if (isValidElement(link)) {
                const elementKey = link.key ?? `breadcrumb-${index}`;
                const element = cloneElement(link, { key: elementKey });

                return (
                  <li className={breadcrumbStyles.breadcrumbsListItem} key={elementKey}>
                    {element}
                    {!isLast && (
                      <Icon
                        name="chevron_right_black"
                        size="sm"
                        className={breadcrumbStyles.icon}
                        aria-hidden="true"
                      />
                    )}
                  </li>
                );
              }

              if (isLayoutBreadCrumbItem(link)) {
                const slug = deriveSlug(link.url);
                const key = `breadcrumb-${slug ?? "forsakring"}-${index}`;

                return (
                  <li className={breadcrumbStyles.breadcrumbsListItem} key={key}>
                    <Breadcrumb
                      breadcrumb={link}
                      slug={slug}
                      current={isLast ? "page" : undefined}
                    />
                    {!isLast && (
                      <Icon
                        name="chevron_right_black"
                        size="sm"
                        className={breadcrumbStyles.icon}
                        aria-hidden="true"
                      />
                    )}
                  </li>
                );
              }

              const fallbackKey = `breadcrumb-${index}`;

              return (
                <li className={breadcrumbStyles.breadcrumbsListItem} key={fallbackKey}>
                  <span>{link as ReactNode}</span>
                  {!isLast && (
                    <Icon
                      name="chevron_right_black"
                      size="sm"
                      className={breadcrumbStyles.icon}
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </BreadcrumbsWrapper>
  );
};

export default AfaBreadCrumbs;
